import React, { useState, useRef } from 'react';
import api from '../utils/api';
import { X, Upload, Download, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

function parseCSV(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  function parseLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
      else { current += ch; }
    }
    result.push(current.trim());
    return result;
  }

  const headers = parseLine(lines[0]).map(h => h.replace(/"/g, '').trim());
  return lines.slice(1).map(line => {
    const values = parseLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (values[i] || '').replace(/"/g, '').trim(); });
    return obj;
  }).filter(row => row['Name'] || row['Mobile'] || row['name'] || row['mobile']);
}

export default function CSVUploadModal({ onClose, onSave, users }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const consultants = (users || []).filter(u => u.role === 'consultant' && u.active !== false);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const token = localStorage.getItem('crm_token');

  const handleFile = (f) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const rows = parseCSV(e.target.result);
      setPreview(rows.slice(0, 5));
    };
    reader.readAsText(f);
  };

  const upload = async () => {
    if (!file) return toast.error('CSV file select karo');
    setSaving(true);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      const r = await api.post('/leads/bulk', { rows, assignedTo });
      setResult(r.data);
      if (r.data.created > 0) toast.success(`${r.data.created} leads imported!`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div className="modal-title">📤 Bulk CSV Upload</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {!result ? (
            <>
              {/* Download template */}
              <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 13, color: '#1d4ed8' }}>Pehle template download karo — sahi format mein data bharo</div>
                <a href={`${apiUrl}/leads/csv-template?token=${token}`} className="btn btn-ghost btn-sm" download>
                  <Download size={13} /> Template
                </a>
              </div>

              {/* CSV Headers info */}
              <div style={{ padding: '8px 12px', background: 'var(--surface)', borderRadius: 8, marginBottom: 16, fontSize: 12, color: 'var(--text-muted)' }}>
                <strong>Required columns:</strong> Name, Mobile | Optional: Email, Personal Loan, Credit Card, Monthly EMI, City, Employment Status, Description, Alternate WhatsApp, Preferred Time, Harassment Status
              </div>

              {/* File upload */}
              <div
                style={{ border: '2px dashed var(--border)', borderRadius: 8, padding: 24, textAlign: 'center', cursor: 'pointer', background: file ? '#f0fdf4' : 'var(--surface)', marginBottom: 16 }}
                onClick={() => fileRef.current.click()}
              >
                <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
                <Upload size={24} style={{ color: file ? 'var(--success)' : 'var(--text-light)', marginBottom: 8 }} />
                {file ? (
                  <div style={{ color: 'var(--success)', fontWeight: 600 }}>✓ {file.name}</div>
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Click to upload CSV file</div>
                )}
              </div>

              {/* Assign to */}
              <div className="form-group">
                <label className="form-label">Assign all leads to</label>
                <select className="form-control" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
                  <option value="">Assign to me</option>
                  {consultants.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>

              {/* Preview */}
              {preview.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text-muted)' }}>Preview (first 5 rows):</div>
                  <div style={{ overflowX: 'auto', fontSize: 11 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {Object.keys(preview[0]).slice(0, 6).map(h => (
                            <th key={h} style={{ padding: '4px 8px', background: 'var(--surface)', textAlign: 'left', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((row, i) => (
                          <tr key={i}>
                            {Object.values(row).slice(0, 6).map((v, j) => (
                              <td key={j} style={{ padding: '4px 8px', border: '1px solid var(--border)', whiteSpace: 'nowrap', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>{result.created > 0 ? '✅' : '⚠️'}</div>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Import Complete</div>
              <div className="grid grid-3" style={{ gap: 10 }}>
                <div style={{ padding: 12, background: '#f0fdf4', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--success)' }}>{result.created}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Created</div>
                </div>
                <div style={{ padding: 12, background: '#fff7ed', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--warning)' }}>{result.duplicates}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Duplicates</div>
                </div>
                <div style={{ padding: 12, background: result.errors.length > 0 ? '#fef2f2' : 'var(--surface)', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: result.errors.length > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>{result.errors.length}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Errors</div>
                </div>
              </div>
              {result.errors.length > 0 && (
                <div style={{ marginTop: 12, padding: 10, background: '#fef2f2', borderRadius: 8, fontSize: 12, textAlign: 'left' }}>
                  {result.errors.slice(0, 5).map((e, i) => <div key={i} style={{ color: 'var(--danger)' }}>{e}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={() => { if (result) onSave(); else onClose(); }}>
            {result ? 'Done' : 'Cancel'}
          </button>
          {!result && (
            <button className="btn btn-primary" onClick={upload} disabled={saving || !file}>
              {saving ? <span className="spinner" /> : `📤 Import ${preview.length > 0 ? '...' : 'CSV'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
